/**
 * The page data the SEO server already read for a home, list or hub URL
 * (web plan W4.5), keyed by the GraphQL query the page runs for it.
 *
 * The server's decision (server/routes.mjs `decide({ withPreload })`) sends
 * the same printed documents with the same variables the page's hooks build
 * (`listVariables` = `useRichList`'s, `categoryPageVariables`, …), so one key
 * names one answer on both sides: `preloadKey(operationName, variables)`.
 * The value is the query's `data` (`{ publishedBrands: {…} }`). The route
 * module hands the map to the page (@/hooks/routePreload), whose hooks show
 * it instead of a loading state, on the server and in the render that
 * hydrates it.
 *
 * Anonymous data only: the server asks the backend as a guest, and the HTML
 * is shared through the cache. A signed-in viewer's own copy (their
 * `isFavorite`) replaces it after mount.
 *
 * Pure module: no `window`, no React (the server bundle imports it).
 */

/** Query answers by `preloadKey`. */
export type RoutePreloadData = Record<string, unknown>;

/** JSON with object keys sorted at every level: the same variables always print the same. */
export function stableJson(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .filter((key) => record[key] !== undefined)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`);
  return `{${entries.join(',')}}`;
}

/** `PublishedBrandsRich {"options":{…}}`: the key of one query's answer. No variables count as `{}`. */
export function preloadKey(operationName: string, variables?: unknown): string {
  return `${operationName} ${stableJson(variables ?? {})}`;
}

/** The answer `preload` holds for this query, or undefined. */
export function preloadedAnswer(
  preload: RoutePreloadData | null | undefined,
  operationName: string | null | undefined,
  variables?: unknown,
): unknown {
  if (!preload || !operationName) return undefined;
  const key = preloadKey(operationName, variables);
  return Object.hasOwn(preload, key) ? preload[key] : undefined;
}
